/**
 * DealOriginBadge
 * ----------------
 * Exibe a origem do CADASTRO de um negócio (como ele entrou no sistema):
 * manual, importação CSV, webhook, agendamento online, fluxo ou API.
 *
 * É diferente de `deal.source` — que representa a origem COMERCIAL do lead
 * (Instagram, WhatsApp, Site etc.).
 *
 * Os dados vêm da coluna `crm_deals.creation_origin` (JSONB) gravada
 * server-side por `create_crm_deal_with_check` / `create_crm_deal` /
 * `process_deal_import_batch` / `create_scheduling_booking`.
 *
 * CHANGELOG:
 *   2026-05-16 v1.1.0 - Tooltip expansível (Ver mais/Ver menos) usando Popover
 *                       no hover, evitando estouro de largura no badge colapsado
 *                       e permitindo inspecionar todos os campos de origem.
 *   2026-05-16 v1.0.0 - Criação inicial.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Upload, Webhook, CalendarClock, Workflow, User, Plug, Handshake, ChevronDown, ChevronUp } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';

/** Schema do JSON gravado em `creation_origin`. */
export interface CreationOrigin {
  type: 'manual' | 'import' | 'webhook' | 'scheduling' | 'flow' | 'api' | 'affiliate';
  source_id?: string | null;
  source_name?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra?: Record<string, any> | null;
}

/** Configuração visual por tipo de origem. */
const ORIGIN_CONFIG: Record<
  CreationOrigin['type'],
  { label: string; icon: typeof Upload; className: string }
> = {
  manual: {
    label: 'Manual',
    icon: User,
    className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  },
  import: {
    label: 'Importação',
    icon: Upload,
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-200',
  },
  webhook: {
    label: 'Webhook',
    icon: Webhook,
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-200',
  },
  scheduling: {
    label: 'Agendamento',
    icon: CalendarClock,
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-200',
  },
  flow: {
    label: 'Fluxo',
    icon: Workflow,
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-200',
  },
  api: {
    label: 'API',
    icon: Plug,
    className: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200',
  },
  // v1.3.0 (2026-05-16): Afiliado — negócios vindos do módulo Indicação
  affiliate: {
    label: 'Afiliado',
    icon: Handshake,
    className: 'bg-pink-100 text-pink-700 dark:bg-pink-950/60 dark:text-pink-200',
  },
};

/** Regex simples para detectar UUID v4 em campos source_id/extra. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Trunca UUID para 8 chars uppercase (padrão de UI do projeto). */
function formatPossibleUuid(value: unknown): string {
  const s = String(value ?? '');
  return UUID_RE.test(s) ? s.slice(0, 8).toUpperCase() : s;
}

/** Props do componente. */
interface DealOriginBadgeProps {
  /** ID do negócio. Quando informado, o badge busca a origem por si só. */
  dealId?: string;
  /** Origem já carregada (evita query extra). Tem prioridade sobre dealId. */
  origin?: CreationOrigin | null;
  /** Esconde o label textual e mostra só o ícone. */
  iconOnly?: boolean;
  /** Largura máxima do texto interno do badge (px). Default 160. */
  maxTextWidth?: number;
  className?: string;
}

/**
 * Badge da origem do cadastro do negócio.
 *
 * Renderiza um pill colorido com ícone + rótulo curto, e ao passar o mouse
 * abre um HoverCard com modo resumido (label + nome). Possui botão
 * "Ver mais/Ver menos" que expande para mostrar todos os campos de origem
 * (source_id formatado em 8 chars uppercase + pares do `extra`). Retorna
 * `null` se a origem for desconhecida (negócios legados).
 */
export function DealOriginBadge({
  dealId,
  origin: originProp,
  iconOnly = false,
  maxTextWidth = 160,
  className,
}: DealOriginBadgeProps) {
  // Busca creation_origin direto da tabela (coluna pública via RLS de leitura).
  // Mantém o componente self-contained: o consumidor só precisa do dealId.
  const { data } = useQuery({
    queryKey: ['deal-creation-origin', dealId],
    enabled: !originProp && !!dealId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_deals')
        .select('creation_origin')
        .eq('id', dealId!)
        .maybeSingle();
      if (error) throw error;
      return (data?.creation_origin as unknown as CreationOrigin | null) ?? null;
    },
  });

  const origin = originProp ?? data ?? null;
  // Estado de expansão do tooltip — controlado localmente, não fecha o hover.
  const [expanded, setExpanded] = React.useState(false);

  if (!origin || !origin.type) return null;

  const config = ORIGIN_CONFIG[origin.type] ?? ORIGIN_CONFIG.api;
  const Icon = config.icon;
  const detail = origin.source_name?.trim() || null;

  // Pares válidos do `extra` (filtra null/undefined/string vazia).
  const extraEntries = origin.extra
    ? Object.entries(origin.extra).filter(([, v]) => v !== null && v !== undefined && v !== '')
    : [];

  // Há conteúdo extra para justificar o botão "Ver mais"?
  const hasMore = Boolean(origin.source_id) || extraEntries.length > 0;

  return (
    <HoverCard openDelay={150} closeDelay={120} onOpenChange={(o) => !o && setExpanded(false)}>
      <HoverCardTrigger asChild>
        <Badge
          variant="outline"
          className={cn(
            'inline-flex items-center gap-1 border-transparent text-[10px] font-medium px-2 py-0.5 cursor-default',
            config.className,
            className,
          )}
        >
          <Icon className="h-3 w-3 shrink-0" />
          {!iconOnly && (
            <span className="truncate" style={{ maxWidth: maxTextWidth }}>
              {config.label}
              {detail ? ` · ${detail}` : ''}
            </span>
          )}
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent
        side="bottom"
        align="start"
        className="max-w-sm w-auto p-3 text-xs"
      >
        <div className="space-y-1.5">
          <p className="font-semibold text-sm">Origem do cadastro: {config.label}</p>
          {detail && (
            <p className="text-muted-foreground break-words">{detail}</p>
          )}

          {expanded && (
            <div className="mt-2 pt-2 border-t space-y-1 max-h-[280px] overflow-auto">
              {origin.source_id && (
                <div className="flex gap-1.5">
                  <span className="text-muted-foreground/80 font-medium">ID:</span>
                  <span className="font-mono break-all">{formatPossibleUuid(origin.source_id)}</span>
                </div>
              )}
              {extraEntries.map(([k, v]) => (
                <div key={k} className="flex gap-1.5">
                  <span className="text-muted-foreground/80 font-medium">{k}:</span>
                  <span className="break-all">{formatPossibleUuid(v)}</span>
                </div>
              ))}
            </div>
          )}

          {hasMore && (
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline focus:outline-none focus:underline"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3 w-3" /> Ver menos
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" /> Ver mais
                </>
              )}
            </button>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
